import { Box, ClickAwayListener, IconButton, ListItemText, MenuItem, Paper, Popover, Popper, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import GenericFloatingButton from '../../generic/buttons/floatingButtons/GenericFloatingButton'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { AddCircleOutlineRounded, EditRounded, HistoryOutlined, MoreVert, SaveRounded, SchemaOutlined, SearchOutlined } from '@mui/icons-material'
import { useTheme } from '@emotion/react'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import Structure from './Structure'
import ImportFile from '../../importFile'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import WorkflowTable from './WorkflowTable'
import { useToggle } from '../../generic/hooks/useToggle'
import createDialogTitle from '../../generic/utilities/dialogUtil'
import { apiGet } from '../../generic/Api_Functions'
import useFrontEndConfig from '../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import { formatPartReference } from '../orders/partReferenceLabel'
import { SmartSelect } from '../../generic/smartSelect'
import WorkflowPartReferenceDialog from './WorkflowPartReferenceDialog'
import { useAuthorization } from '../../generic/hooks/useAuthorization'
import { getRandomUUID } from '../../generic/utilities/randomuuid'
import SmartTextField from '../../generic/smarttextfield/SmartTextField'
import MaterialStructure from './WorkflowStructure'
import { applyCascadeApproval, collectCascadePendingItems } from './cascadeApproval'
import { loadFullWorkflowTree } from './cascadeWorkflowLoader'
import CascadeIndexChangeDialog from './dialogs/CascadeIndexChangeDialog'
import WorkflowSaveAsTemplateDialog from './WorkflowSaveAsTemplateDialog'
import { workflowOriginTextOf } from '../../generic/utilities/workflowOrigin'
import { formatCodeName } from '../../generic/utilities/objectStructures/codeNameFormat'
import TemplateLinkLabel from './templateLinks/TemplateLinkLabel'

/**
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowEditingDataAction} WorkflowEditingDataAction EditingDataAction object
 * @typedef {import('../../generic/utilities/objectStructures/workflow/workflow').WorkflowEditingData}  WorkflowEditingData WorkflowEditingData object
 */

/**
 *
 * @param {Object} Props
 * @param {WorkflowEditingData} Props.workflowEditingData
 * @param {React.Dispatch<WorkflowEditingDataAction>} Props.dispatchWorkflowData
 * @param {Function} Props.setSaveDialogOpen
 * @param {Function} Props.onRevert
 * @param {Function} Props.onAddRecord
 * @param {import('../../generic/utilities/objectStructures/globalStructures').IdName[]} Props.indexValues
 * @param {boolean} Props.workflowUseIndex
 * @param {boolean} Props.isSaving Historia 161 (RN15/RN17/DL6): a workflow save (template route) is in flight —
 *   Save and Revert stay disabled (the spinner and `Saving` live on the editor's overlay). Does not apply to the OPERATIONS
 *   library's own Save/Revert (a different flow, untouched by this story).
 * @returns
 */
const WorkflowHeader = (
  {
    workflowEditingData,
    dispatchWorkflowData,
    setSaveDialogOpen,
    onRevert,
    onAddRecord,
    hideButtonsSubWorkflow = false,
    fromFO = false,
    onEditModeChange,
    indexValues,
    workflowUseIndex,
    cascadeApprovalEnabled = false,
    // Story 186: CONTROLLED feature group revisions; the cascade then leaves the groups already at or above the target (RN49)
    featureGroupRevisionControlled = false,
    allowEdit,
    // Historia 130: en la vista OPERATIONS la cabecera es la de la BIBLIOTECA, no la del workflow.
    // Los campos y las acciones del workflow no aplican ahí y se retiran o se deshabilitan.
    isOperationsView = false,
    operationsEditing = false,
    isSaving = false,
    /*
     * RN117.1 (story #150): false while any table on screen holds a row open. The prop was already
     * being handed in and simply not declared, so the save it was meant to block went through and the
     * half-typed row was silently dropped.
     */
    saveAllowed = true,
    saveBlockedReason = '',
    // Story 199: the editor's own alert, so "Save as template" confirms where every other editor message shows.
    showAlert = () => {},
    // Story 224 (S103, S105): the ROOT template link of the workflow (E1); `{}` when it has none.
    templateLink = {}
  }) => {
  const { t_ } = useSmartTranslation()
  // Historia 125 (MIC-4261), delta del PO: the part reference reads in the installation's format,
  // through the same helper the request form uses. Painting and resolving with ONE format is what
  // keeps a chosen option findable (E4).
  const [frontEndConfig] = useFrontEndConfig()
  const partReferenceDisplayFormat = frontEndConfig?.partReferenceDisplayFormat
  const [showStructure, setShowStructure] = useState(false)
  const [showActions, setShowActions] = useState(false)
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false)
  const [showSaveAsTemplate, setShowSaveAsTemplate] = useState(false)
  const [showMaterialStructure, setShowMaterialStructure] = useState(false)
  const [resetSelectedRow, toggleResetSelectedRow] = useToggle()
  const [selectedRow, setSelectedRow] = useState(null)
  const [partReferences, setPartReferences] = useState([])
  const structurePosition = useRef()
  const actionsPosition = useRef(null)
  const theme = useTheme()
  const [optionPartreferences, setOptionPartreferences] = useState([])
  const [openTemplateSelectDialog, setOpenTemplateSelectDialog] = useState(false)
  const isUserAuthorized = useAuthorization()
  // Historia 130 (S1/S2, CA1.1 / RN-01.1, Δ1): la biblioteca de operaciones es la TERCERA opción de
  // este mismo selector, no una pantalla aparte. Sin `OPERATION_LIBRARY` no se ofrece (RN-01.9); el
  // gating es de UI: la autoridad del permiso es Sun, que responde 403 a las 16 rutas (RN-G15).
  const selectableOptions = [
    { code: 'TEMPLATE', name: t_('Template') },
    { code: 'WORKFLOW', name: t_('Workflow') },
    ...(isUserAuthorized('OPERATION_LIBRARY') ? [{ code: 'OPERATIONS', name: t_('Operations') }] : [])
  ]
  const [firstLoad, setFirstLoad] = useState(true)
  /** @type {[import('../../generic/utilities/objectStructures/extendedcolumn/extendedColumn').ExtendedColumn[], React.Dispatch<SetStateAction<import('../../generic/utilities/objectStructures/extendedcolumn/extendedColumn').ExtendedColumn[]>>]} */
  const [extendedColumns, setExtendedColumns] = useState([])

  useEffect(() => {
    const getData = async () => {
      const data = await apiGet('extendedColumn/workflow')
      setExtendedColumns(data)
      setFirstLoad(false)
    }
    getData()
  }, [firstLoad])

  const loadWorkflow = async (workflowId, editMode, locked) => {
    const response = await apiGet('workflow/' + workflowId)
    response.tempWorkflowUUID = getRandomUUID()
    if (!response || response.error) {
      return
    }
    dispatchWorkflowData({ type: 'SET_WORKFLOW', workflow: response, editing: editMode, locked })
    if (editMode) {
      apiGet('workflows/ping/' + workflowId)
    }
  }
  const handleOnWorkflowSelected = async () => {
    if (!selectedRow) return
    await loadWorkflow(selectedRow.id, !selectedRow.locked, selectedRow.locked)
    setShowWorkflowDialog(false)
  }

  useEffect(() => {
    const fetchReferences = async () => {
      try {
        const references = await apiGet('partReferencesShortData')
        setPartReferences(references)
        setOptionPartreferences(references.map(pr => formatPartReference(pr, partReferenceDisplayFormat)))
      } catch (err) {
        console.error('Error fetching part references:', err)
      }
    }
    fetchReferences()
  }, [partReferenceDisplayFormat])

  const onAddRecordClick = () => {
    if (workflowEditingData.type?.code === 'WORKFLOW') {
      setOpenTemplateSelectDialog(true)
      return
    }
    onAddRecord()
    toggleResetSelectedRow()
  }

  const beanParams = [
    {
      bean: {
        beanPackage: 'com.smartpm.sun.dto.workflowwindow',
        beanName: 'Workflow',
        beanDtoPackage: 'com.smartpm.sun.usecase.importworkflows.infrastructure.rest',
        beanDtoName: 'ImportWorkflowLine',
        servicePackage: 'com.smartpm.sun.usecase.importworkflows.application',
        serviceName: 'WorkflowImportService'
      },
      name: t_('Workflow')
    }
  ]

  // Warn before leaving the page (refresh, close tab, navigate away)
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (workflowEditingData.editing) {
        e.preventDefault()
        e.returnValue = '' // Chrome requires returnValue to be set
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [workflowEditingData.editing])

  const getExtendedColumns = () => {
    if (!extendedColumns || extendedColumns.length === 0) {
      <></>
    }

    return extendedColumns.map(extendedColumn => {
      if (extendedColumn.dataType === 'LIST') {
        return (
          <SmartSelect
            key={extendedColumn.label}
            label={extendedColumn.label}
            value={!workflowEditingData.workflow[extendedColumn.field] ? '' : workflowEditingData.workflow[extendedColumn.field]}
            onChange={(value) => {
              const newWorkflow = structuredClone(workflowEditingData.workflow)
              newWorkflow[extendedColumn.field] = value
              dispatchWorkflowData({
                type: 'UPDATE_WORKFLOW',
                workflow: newWorkflow
              })
            }}
            selectableOptions={extendedColumn.options}
            disabled={!workflowEditingData.editing}
            disableClearable
            minWidth='13rem'
          />
        )
      }

      return (
        <SmartTextField
          key={extendedColumn.label}
          label={extendedColumn.label}
          inputProps={{ maxLength: 255 }}
          value={!workflowEditingData.workflow[extendedColumn.field] ? '' : workflowEditingData.workflow[extendedColumn.field]}
          disabled={!workflowEditingData.editing}
          onChange={(newValue) => {
            const newWorkflow = structuredClone(workflowEditingData.workflow)
            newWorkflow[extendedColumn.field] = newValue
            dispatchWorkflowData({
              type: 'UPDATE_WORKFLOW',
              workflow: newWorkflow
            })
          }}
        />
      )
    })
  }

  // Enmienda PO 23/07/2026 (HU-04/HU-05): with WORKFLOW_CASCADE_APPROVAL active the workflow
  // Index selector is NOT filtered by its children's states; changing it to a value some child
  // does not match opens a confirmation modal instead. Accepting cascades the selected value to
  // every subworkflow/job/featureGroup; cancelling keeps everything at its previous value.
  // The whole tree is loaded FIRST (nested subworkflows included) so the cascade reaches the
  // nodes inside every subworkflow — otherwise opening one afterwards re-fetches the persisted
  // (previous) indexValues from the backend.
  const [cascadeDialog, setCascadeDialog] = useState({ open: false, targetIndexValue: {}, items: [], workflow: null })

  const changeWorkflowIndexValue = async (value) => {
    const found = indexValues.find(indexValue => indexValue.name === value)
    if (cascadeApprovalEnabled) {
      const fullWorkflow = await loadFullWorkflowTree(workflowEditingData.workflow)
      // Story 186 (RN49): the cascade never blocks; the dialog warns about the feature groups still in DRAFT.
      const items = collectCascadePendingItems(fullWorkflow, found.name, { controlled: featureGroupRevisionControlled })
      if (items.length > 0) {
        setCascadeDialog({ open: true, targetIndexValue: found, items, workflow: fullWorkflow })
        return
      }
    }
    const newWorkflow = structuredClone(workflowEditingData.workflow)
    newWorkflow.indexValue = found
    dispatchWorkflowData({ type: 'UPDATE_WORKFLOW', workflow: newWorkflow })
  }

  const getAvailableIndexes = () => {
    if (indexValues.length === 0) {
      return []
    }
    if (cascadeApprovalEnabled || !workflowEditingData.workflow?.nodes) {
      return indexValues.map(indexValue => indexValue.name)
    }

    const availables = []
    if (indexValues.find(index => index.name === 'DRAFT')) {
      availables.push('DRAFT')
    }

    if (workflowEditingData.workflow?.nodes.filter(node =>
      (node.nodeType === 'JOB' && node.jobReference.indexValue.name === 'DRAFT') ||
      (node.nodeType === 'WORKFLOW' && node.subWorkflow.indexValue.name === 'DRAFT')).length === workflowEditingData.workflow.nodes.length) {
      return availables
    }

    if (workflowEditingData.workflow?.nodes.filter(node =>
      (node.nodeType === 'JOB' && node.jobReference.indexValue.name === 'PROPOSAL') ||
      (node.nodeType === 'WORKFLOW' && node.subWorkflow.indexValue.name === 'PROPOSAL')).length === workflowEditingData.workflow.nodes.length) {
      availables.push('PROPOSAL')
      return availables
    }

    if (workflowEditingData.workflow?.nodes.filter(node =>
      (node.nodeType === 'JOB' && node.jobReference.indexValue.name === 'APPROVED') ||
      (node.nodeType === 'WORKFLOW' && node.subWorkflow.indexValue.name === 'APPROVED')).length === workflowEditingData.workflow.nodes.length) {
      availables.push('PROPOSAL')
      availables.push('APPROVED')
      return availables
    }

    if (workflowEditingData.workflow?.nodes.filter(node =>
      (node.nodeType === 'JOB' && (node.jobReference.indexValue.name === 'PROPOSAL' || node.jobReference.indexValue.name === 'APPROVED')) ||
      (node.nodeType === 'WORKFLOW' && (node.subWorkflow.indexValue.name === 'PROPOSAL' || node.subWorkflow.indexValue.name === 'APPROVED'))).length === workflowEditingData.workflow.nodes.length) {
      availables.push('PROPOSAL')
    }
    return availables
  }

  const originText = workflowOriginTextOf(workflowEditingData.workflow?.origin, t_)
  // Story 199 (POL1, S65-S67)
  const saveAsTemplateDisabled = isOperationsView || workflowEditingData.editing || !workflowEditingData.workflow?.id ||
    !workflowEditingData.partReference || !!workflowEditingData.workflow?.linkedToFabricationOrder ||
    !!workflowEditingData.workflow?.onlySubWorkflow || !isUserAuthorized('WORKFLOWS')

  // H187 D7 (S74, PO 2026-10-05): with the wider Code the FO editor row may not fit. Only in that branch the row wraps
  // (fields that do not fit go to a second line, none is lost) and its column can shrink; every other header keeps its grid.
  const wrapsRow = hideButtonsSubWorkflow && fromFO

  return (
    <div style={{ display: 'grid', gridTemplateColumns: wrapsRow ? 'minmax(0, 1fr) auto max-content' : 'max-content 1fr max-content' }}>
      <Box
        data-testid='workflow-header-row'
        style={wrapsRow
          ? { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '1rem' }
          : { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr max-content max-content', gap: '1rem' }}
        sx={wrapsRow ? { '& > *': { flex: '1 1 14rem' } } : undefined}
      >
        {!hideButtonsSubWorkflow
          ? (
            <>
              <SmartSelect
                label={t_('Type')}
                value={t_(workflowEditingData.type?.name)}
                onChange={(value) => {
                  const selectedOption = selectableOptions.find(
                    (option) => t_(option.name) === value
                  )
                  dispatchWorkflowData({
                    type: 'UPDATE_TYPE',
                    editionType: selectedOption
                  })
                }}
                selectableOptions={selectableOptions.map((option) => t_(option.name))}
                // S10: dentro de OPERATIONS el selector SIEMPRE deja volver. Bloquearlo por el modo
                // edición del workflow suspendido dejaba al usuario encerrado en la biblioteca.
                disabled={workflowEditingData.editing && !isOperationsView}
                disableClearable
                minWidth='13rem'
              />
              {/* Historia 130: código, nombre, descripción, versión, index y referencia de pieza son
                  del WORKFLOW. En la vista OPERATIONS no hay workflow que editar, así que no se
                  pintan: lo que se edita ahí es una versión de la biblioteca. */}
              {isOperationsView
                ? null
                : (
                  <>
                    <SmartSelect
                      label={t_('Part Reference')}
                      error={
                  !workflowEditingData.partReference &&
                  workflowEditingData.type.code !== 'TEMPLATE'
                }
                      value={workflowEditingData.partReference?.code ? formatPartReference(workflowEditingData.partReference, partReferenceDisplayFormat) : null}
                      disabled={workflowEditingData.type.code === 'TEMPLATE' || workflowEditingData.editing}
                      onChange={(value) => {
                        const found = partReferences.find((r) => formatPartReference(r, partReferenceDisplayFormat) === value)
                        dispatchWorkflowData({
                          type: 'UPDATE_PART_REFERENCE',
                          partReference: found
                        })
                      }}
                      selectableOptions={optionPartreferences}
                      autoSelectFirst={false}
                      disableClearable={false}
                      minWidth='13rem'
                    />
                    <TextField
                      fullWidth
                      label={t_('Code')}
                      required
                      value={workflowEditingData.workflow?.code}
                      disabled={!workflowEditingData.editing || workflowEditingData.workflow.id}
                      inputProps={{ maxLength: 255 }}
                      error={workflowEditingData.workflow?.code?.length > 255}
                      onChange={(event) => {
                        const value = event.target.value
                        if (value.length <= 255) {
                          const workflow = workflowEditingData.workflow
                          workflow.code = value
                          dispatchWorkflowData({ type: 'UPDATE_WORKFLOW', workflow })
                        }
                      }}
                    />
                    {/* S58 (RN01.2/RNT5, enmienda 2 GATE 2): new free, descriptive workflow name, next to
                  the code, independently editable and persisted */}
                    <TextField
                      fullWidth
                      label={t_('Name')}
                      value={workflowEditingData.workflow?.name || ''}
                      disabled={!workflowEditingData.editing}
                      inputProps={{ maxLength: 255 }}
                      onChange={(event) => {
                        const value = event.target.value
                        if (value.length <= 255) {
                          const workflow = workflowEditingData.workflow
                          workflow.name = value
                          dispatchWorkflowData({ type: 'UPDATE_WORKFLOW', workflow })
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label={t_('Description')}
                      inputProps={{ maxLength: 255 }}
                      disabled={!workflowEditingData.editing}
                      onChange={(event) => {
                        const workflow = workflowEditingData.workflow
                        workflow.description = event.target.value
                        dispatchWorkflowData({ type: 'UPDATE_WORKFLOW', workflow })
                      }}
                      value={workflowEditingData.workflow?.description}
                    />
                    <Stack direction='row' spacing={1} alignItems='center'>
                      <TextField
                        fullWidth
                        disabled
                        label={t_('Version')}
                        style={{ maxWidth: '5rem' }}
                        value={
                    workflowEditingData.workflow?.code
                      ? workflowEditingData.workflow?.version
                      : ''
                  }
                      />
                      {/* Story 224 (S103, S105): the template this workflow comes from, next to its version. */}
                      <TemplateLinkLabel link={templateLink} />
                    </Stack>
                    {workflowUseIndex
                      ? (<SmartSelect
                          label={t_('Index')}
                          value={workflowEditingData.workflow?.indexValue?.name}
                          disabled={!workflowEditingData.editing}
                          onChange={changeWorkflowIndexValue}
                          selectableOptions={getAvailableIndexes()}
                          autoSelectFirst={false}
                          minWidth='9rem'
                         />)
                      : null}
                  </>
                  )}
            </>
            )
          : (
            <>
              {/* H187 (S52): the copy origin goes in the same grid cell as Code. Only for the workflow of a FO
                  (`fromFO`), never in a sub-workflow dialog or in the workflows window (S57). */}
              <Box sx={fromFO ? { minWidth: '22rem' } : undefined}>
                {fromFO
                  ? (
                    // H187 D7 (S74): a disabled input gets no mouse events, so the span receives them. The full code
                    // goes in the Tooltip and in the input's `title` (never on the Tooltip's direct child: MUI rejects it).
                    <Tooltip title={workflowEditingData.workflow?.code || ''}>
                      <span style={{ display: 'block' }}>
                        <TextField
                          fullWidth
                          label={t_('Code')}
                          value={workflowEditingData.workflow?.code}
                          inputProps={{ title: workflowEditingData.workflow?.code || '' }}
                          disabled
                        />
                      </span>
                    </Tooltip>
                    )
                  : (
                    <TextField
                      fullWidth
                      label={t_('Code')}
                      value={workflowEditingData.workflow?.code}
                      disabled
                    />
                    )}
                {fromFO && originText
                  ? <Typography variant='body2' color='text.secondary'>{originText}</Typography>
                  : null}
              </Box>
              <TextField
                fullWidth
                label={t_('Name')}
                value={workflowEditingData.workflow?.name || ''}
                disabled
              />
              <TextField
                fullWidth
                label={t_('Description')}
                disabled
                value={workflowEditingData.workflow?.description}
              />
              {workflowUseIndex
                ? (<SmartSelect
                    label={t_('Index')}
                    value={workflowEditingData.workflow?.indexValue?.name}
                    disabled={!workflowEditingData.editing}
                    onChange={changeWorkflowIndexValue}
                    selectableOptions={getAvailableIndexes()}
                    autoSelectFirst={false}
                    minWidth='13rem'
                   />)
                : null}
            </>
            )}
        {isOperationsView ? null : getExtendedColumns()}
      </Box>
      <div />
      <Stack direction='row' gap={2} style={{ alignItems: 'start' }}>
        {!hideButtonsSubWorkflow && !isOperationsView && (
          <div ref={actionsPosition}>
            <GenericFloatingButton
              icon={<MoreVert />}
              opacity={1}
              onClick={(_) => {
                setShowActions(prev => !prev)
              }}
              color={theme.palette.primary.main}
            />
          </div>
        )}
        <Popper
          open={showActions}
          anchorEl={actionsPosition.current}
          sx={{ zIndex: 9999 }}
        >
          <ClickAwayListener onClickAway={() => setShowActions(false)}>
            <Paper sx={{ zIndex: 9999 }}>
              <MenuItem onClick={() => setShowStructure(true)} disabled={isOperationsView || !workflowEditingData.workflow?.id || !isUserAuthorized('STRUCTURE')}>
                <IconButton sx={{ pointerEvents: 'none' }}>
                  <AccountTreeIcon />
                </IconButton>
                <ListItemText
                  primary={t_('Structure')}
                  sx={{ color: theme.palette.primary.main }}
                />
              </MenuItem>
              <MenuItem onClick={() => { setShowActions(false); setShowMaterialStructure(true) }} disabled={isOperationsView || workflowEditingData?.workflow?.nodes?.length === 0 || !isUserAuthorized('STRUCTURE')}>
                <IconButton sx={{ pointerEvents: 'none' }}>
                  <SearchOutlined />
                </IconButton>
                <ListItemText
                  primary={t_('Workflow structure')}
                  sx={{ color: theme.palette.primary.main }}
                />
              </MenuItem>
              <MenuItem onClick={() => { setShowWorkflowDialog(true); setShowActions(false) }} disabled={isOperationsView || !isUserAuthorized('WORKFLOWS') || workflowEditingData.editing || (workflowEditingData.type.code === 'WORKFLOW' && !workflowEditingData.partReference)}>
                <IconButton sx={{ pointerEvents: 'none' }}>
                  <SchemaOutlined />
                </IconButton>
                <ListItemText
                  primary={t_('Workflow')}
                  sx={{ color: theme.palette.primary.main }}
                />
              </MenuItem>
              {/* Story 199 (POL1): a persisted part reference workflow, outside edit mode, never an order's own nor a
                  subworkflow living inside another one. */}
              <MenuItem
                onClick={() => { if (!saveAsTemplateDisabled) { setShowActions(false); setShowSaveAsTemplate(true) } }}
                disabled={saveAsTemplateDisabled}
              >
                <IconButton sx={{ pointerEvents: 'none' }}>
                  <SchemaOutlined />
                </IconButton>
                <ListItemText
                  primary={t_('Save as template')}
                  sx={{ color: theme.palette.primary.main }}
                />
              </MenuItem>
              {/* Add authorization to import file disabe={!isUserAuthorized('ADD_FILE_WORKFLOW')} */}
              <ImportFile beanParams={beanParams} selectedIndex={0} menuItemButton disabled />
              <MenuItem onClick={onAddRecordClick} disabled={isOperationsView || !allowEdit || !isUserAuthorized('ADD_WORKFLOW') || workflowEditingData.editing || (workflowEditingData.type.code === 'WORKFLOW' && !workflowEditingData.partReference)}>
                <IconButton sx={{ pointerEvents: 'none' }}>
                  <AddCircleOutlineRounded />
                </IconButton>
                <ListItemText
                  primary={t_('ADD RECORD').split(' ').map(record => record.charAt(0) + record.substring(1, record.length).toLowerCase()).join(' ')}
                  sx={{ color: theme.palette.primary.main }}
                />
              </MenuItem>
            </Paper>
          </ClickAwayListener>
        </Popper>
        <WorkflowSaveAsTemplateDialog
          open={showSaveAsTemplate}
          workflowId={workflowEditingData.workflow?.id}
          defaultCode={workflowEditingData.workflow?.code}
          defaultName={workflowEditingData.workflow?.name}
          onClose={() => setShowSaveAsTemplate(false)}
          onSaved={() => showAlert({ severity: 'success', title: t_('Workflow saved as template') })}
        />
        {/* Historia 130: en la vista OPERATIONS estos dos botones son los de la BIBLIOTECA. Solo
            existen cuando hay una operación cargada —un alta o una versión elegida—, que es cuando
            tienen algo que guardar o que descartar; si no, no se pintan. */}
        {!hideButtonsSubWorkflow && (!isOperationsView || operationsEditing) && (
          <>
            <GenericFloatingButton
              label={t_('Revert')}
              icon={<HistoryOutlined />}
              color={(isOperationsView ? operationsEditing : workflowEditingData.editing) ? theme.palette.error.light : 'disabled'}
              opacity={1}
              onClick={onRevert}
              // S22: el Revert de la vista OPERATIONS es el del ámbito de la biblioteca y no depende
              // del modo edición del workflow suspendido, que ahí no se está tocando.
              // Historia 161 (DL6/RN15): mientras hay un guardado del workflow en vuelo, Revertir se
              // deshabilita también — revertir a mitad de guardado dejaría el editor incoherente con
              // lo que Sun está persistiendo. No aplica a la vista OPERATIONS (flujo distinto).
              disabled={isOperationsView ? !operationsEditing : !workflowEditingData.editing || isSaving}
            />
          </>
        )}
        {!(workflowEditingData.editing && hideButtonsSubWorkflow) && (!isOperationsView || operationsEditing) && (
          <Tooltip title={!saveAllowed ? saveBlockedReason : (!workflowEditingData.workflow?.editable || workflowEditingData.locked ? t_('Not available') : '')}>
            <div>
              <GenericFloatingButton
                // Historia 161: la rueda y el texto "Saving" los muestra la capa que cubre el editor
                // mientras se guarda; aquí el botón solo queda deshabilitado (ver `disabled`).
                label={isOperationsView || workflowEditingData.editing ? t_('Save') : t_('Edit')}
                icon={isOperationsView || workflowEditingData.editing ? <SaveRounded /> : <EditRounded />}
                color={theme.palette.primary.main}
                // K27: en la vista OPERATIONS el permiso de la biblioteca es el único gating. El
                // código del workflow —que ahí puede estar vacío— no tiene nada que decir.
                // Historia 161 (RN15/CA4): con un guardado del workflow en vuelo, Guardar de la
                // cabecera se deshabilita — un segundo clic no puede volver a abrir el diálogo.
                // RN117.4: an open row blocks SAVING, never editing — so it only bites while the
                // button is a Save, and the tooltip above says why.
                disabled={isOperationsView
                  ? !operationsEditing
                  : !allowEdit || (workflowEditingData.workflow?.code === '' && !workflowEditingData.editing) || workflowEditingData.locked || isSaving ||
                    (workflowEditingData.editing && !saveAllowed)}
                opacity={1}
                onClick={() => {
                  if (isOperationsView) {
                    setSaveDialogOpen(true)
                    return
                  }
                  if (workflowEditingData.editing) {
                    setSaveDialogOpen(true)
                  } else {
                    apiGet('workflows/ping/' + workflowEditingData.workflow.id)
                    dispatchWorkflowData({ type: 'SET_EDITING', editing: true })
                    onEditModeChange?.(true)
                  }
                }}
              />
            </div>
          </Tooltip>
        )}
      </Stack>
      <Popover
        open={showStructure}
        anchorEl={structurePosition.current}
        onClose={() => setShowStructure(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
      >
        <Structure partReference={workflowEditingData.workflow?.id} />
      </Popover>
      <SmartDialog
        title={createDialogTitle({ title: t_('Select a workflow'), icon: SchemaOutlined })}
        closeCallback={() => setShowWorkflowDialog(false)}
        accept
        acceptCallback={handleOnWorkflowSelected}
        cancel={false}
        close
        renderComponent={<WorkflowTable
          resetSelectedRow={resetSelectedRow}
          onWorkflowSelected={(row) => { setSelectedRow(row) }}
          partReference={workflowEditingData.partReference}
          currentSelectedeWorkflowId={workflowEditingData.workflow?.id}
                         />}
        setOpen={showWorkflowDialog}
      />
      <SmartDialog
        closeCallback={() => setShowMaterialStructure(false)}
        close
        cancel={false}
        accept={false}
        ignoreChanges
        renderComponent={<MaterialStructure workflow={workflowEditingData.workflow} />}
        setOpen={showMaterialStructure}
      />
      <WorkflowPartReferenceDialog
        partReference={workflowEditingData?.partReference}
        open={openTemplateSelectDialog}
        closeDialog={() => setOpenTemplateSelectDialog(false)}
        loadWorkflow={loadWorkflow}
        toggleResetSelectedRow={toggleResetSelectedRow}
        onWorklowNameChange={(newName) => {
          // RN01.6/RNT5: the identifier typed here is the workflow "code" (was "name")
          const workflow = workflowEditingData.workflow
          workflow.code = newName
          dispatchWorkflowData({ type: 'CREATE_NEW_WITH_NAME', workflowName: newName })
        }}
      />
      <CascadeIndexChangeDialog
        open={cascadeDialog.open}
        subjectLabel={`${t_('Workflow')} ${formatCodeName(workflowEditingData.workflow?.code, workflowEditingData.workflow?.name)}`}
        previousIndexValueName={workflowEditingData.workflow?.indexValue?.name}
        targetIndexValueName={cascadeDialog.targetIndexValue.name}
        items={cascadeDialog.items}
        onAccept={() => {
          const newWorkflow = applyCascadeApproval(cascadeDialog.workflow || workflowEditingData.workflow, indexValues, cascadeDialog.targetIndexValue.name, { controlled: featureGroupRevisionControlled })
          newWorkflow.indexValue = cascadeDialog.targetIndexValue
          dispatchWorkflowData({ type: 'UPDATE_WORKFLOW', workflow: newWorkflow })
          setCascadeDialog({ open: false, targetIndexValue: {}, items: [], workflow: null })
        }}
        onCancel={() => {
          setCascadeDialog({ open: false, targetIndexValue: {}, items: [], workflow: null })
        }}
      />
    </div>
  )
}

export default WorkflowHeader
